import { useMutation } from '@apollo/client';
import {
  CrewMemberExt,
  SuspenseLoader,
  useAppUser,
  useCrewChangeSubscriptionContext,
  useName,
} from '@bechtel/client-common';
import { ADD_CREW_CHANGE_REQUEST } from '@bechtel/client-gql';
import {
  AvatarSize,
  Box,
  ButtonAppearance,
  Icon,
  IconSize,
  SegmentedControlOption,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSize,
  Spinner,
  Text,
  TextArea,
  TextFieldSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  AddCrewChangeRequestInput,
  AddCrewChangeRequestPayload,
  CrewChangeRequestMoveType,
  CrewChangeRequestStatusCode,
  CrewMember,
  PersonaType,
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { concat, find, forEach, get, map, noop } from 'lodash';
import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useCrewChangePermissions } from '../hooks/use-crew-change-permission';
import { useCrewMember } from '../hooks/use-crew-member';
import { CraftList } from './craft-list';
import { CrewSelection } from './crew-selection';
import { AddCrewChangeRequestCustomError, AddCrewChangeRequestErrorExt } from './error/types';
import { MockCrewMembers } from './form-state';
import { CrewChangeRequestPopover } from './pop-over';
import {
  ColStyled,
  CommentStyled,
  CrewListStyled,
  FooterButtonStyled,
  HeaderStyled,
  SegmentedControlStyled,
} from './styled';
import { CrewChangeSummary } from './summary';
import { CrewChangeRequestProps, MockForemanId, MockGeneralForemanId, MockSuperintendentId } from './types';

// const CrewChangeRequestError = lazy(() => import('./error'));
import CrewChangeRequestError from './error';

const CrewChangeRequest: FC<CrewChangeRequestProps> = ({ closeSideSheet }) => {
  const { user } = useAppUser();
  const { canSelectGeneralForeman, canSelectSuperintendent, isUserPartOfCrew } = useCrewChangePermissions();
  const { getFullName } = useName();
  const { getCrewMemberExt } = useCrewMember();
  const [showRequestInProgress, setShowRequestInProgress] = useState<boolean>(false);
  const refElement = useRef(null);
  const [selectedSourceForeman, setSelectedSourceForeman] = useState<CrewMemberExt>(MockCrewMembers[2]);
  const [selectedSourceGeneralForeman, setSelectedSourceGeneralForeman] = useState<CrewMemberExt>(MockCrewMembers[1]);
  const [selectedSourceSuperintendent, setSelectedSourceSuperintendent] = useState<CrewMemberExt>(MockCrewMembers[0]);
  const [selectedDestForeman, setSelectedDestForeman] = useState<CrewMemberExt>(MockCrewMembers[2]);
  const [selectedDestGeneralForeman, setSelectedDestGeneralForeman] = useState<CrewMemberExt>(MockCrewMembers[1]);
  const [selectedDestSuperintendent, setSelectedDestSuperintendent] = useState<CrewMemberExt>(MockCrewMembers[0]);
  const [selectedCrafts, setSelectedCrafts] = useState<CrewMember[]>([]);
  const [crewChangeRequestMoveType, setCrewChangeRequestMoveType] = useState<CrewChangeRequestMoveType>(
    CrewChangeRequestMoveType.CRAFT
  );
  const [comments, setComments] = useState<string>();
  const [hasAnyPendingRequest, setHasAnyPendingRequest] = useState<boolean>(false);
  const [crewChangeRequestError, setCrewChangeRequestError] = useState<AddCrewChangeRequestErrorExt | null>();
  const { close: closeErrorModal, open: openErrorModal } = useModalState(false);
  const { i18n } = useLingui();

  const moveEntireCrew = crewChangeRequestMoveType === CrewChangeRequestMoveType.CREW;
  const userCrewMember = user?.crewMember;
  const hasPendingEntireCrewChangeRequest = selectedSourceForeman?.hasPendingCrewChangeRequest
    ? selectedSourceForeman?.hasPendingCrewChangeRequest
    : false;

  const { affectedCrewMembers, setAffectedCrewMembers } = useCrewChangeSubscriptionContext();

  useEffect(() => {
    if (!(userCrewMember?.persona === PersonaType.FOREMAN || userCrewMember?.persona === PersonaType.GENERAL_FOREMAN))
      return;
    const userCrewMemberSupExt: CrewMemberExt = getCrewMemberExt(userCrewMember?.supervisor);
    if (userCrewMember?.persona === PersonaType.FOREMAN) {
      const supUserCrewMemberSupExt = getCrewMemberExt(userCrewMember?.supervisor?.supervisor);
      setSelectedSourceSuperintendent(supUserCrewMemberSupExt);
      setSelectedDestSuperintendent(supUserCrewMemberSupExt);
      setSelectedSourceGeneralForeman(userCrewMemberSupExt);
      setSelectedDestGeneralForeman(userCrewMemberSupExt);
    }
    if (userCrewMember?.persona === PersonaType.GENERAL_FOREMAN) {
      setSelectedSourceSuperintendent(userCrewMemberSupExt);
      setSelectedDestSuperintendent(userCrewMemberSupExt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCrewMember]);

  useEffect(() => {
    if (moveEntireCrew) {
      setSelectedDestForeman(selectedSourceForeman);
    } else {
      setSelectedDestForeman(MockCrewMembers[2]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveEntireCrew]);

  const CrewChangeRequestPendingApprovalToast = () => {
    let approvalSuperintendent;
    if (userCrewMember?.persona === PersonaType.SUPER_ADMIN || userCrewMember?.id === selectedSourceSuperintendent.id)
      approvalSuperintendent = selectedDestSuperintendent;
    else approvalSuperintendent = selectedSourceSuperintendent;

    const approvalSuperintendentName = getFullName(approvalSuperintendent.firstName, approvalSuperintendent.lastName);

    return (
      <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '450' }}>
          <Text family="graphik" weight="600" size="500">
            <Trans>Your crew change request has been successfully submitted.</Trans>
          </Text>
        </Box>
        <Text family="graphik" weight="600" size="500">
          {t`It will be sent to ${approvalSuperintendentName} for approval.`}
        </Text>
      </Box>
    );
  };

  const CrewChangeRequestApprovedToast = () => {
    return (
      <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Your crew change request has been successfully submitted and approved.</Trans>
        </Text>
      </Box>
    );
  };

  function createSuccessToast(requestStatus: CrewChangeRequestStatusCode): ToastAction {
    const toastAction: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.INFO,
        content:
          requestStatus === CrewChangeRequestStatusCode.PENDING_APPROVAL
            ? CrewChangeRequestPendingApprovalToast
            : CrewChangeRequestApprovedToast,
        autoDismissTimeout: 10000,
        alertAction: {
          label: t`OKAY`,
          onClickHandler: () => noop,
        },
      },
    };
    return toastAction;
  }

  const dispatchToast = useToastsDispatch();
  const [addCrewChangeRequest, { loading }] = useMutation<
    { addCrewChangeRequest: AddCrewChangeRequestPayload },
    { input: AddCrewChangeRequestInput }
  >(ADD_CREW_CHANGE_REQUEST, {
    onCompleted: (data) => {
      const addCrewChangeRequestError = get(data, 'addCrewChangeRequest.error');
      const crewChangeRequest = get(data, 'addCrewChangeRequest.crewChangeRequest');
      if (addCrewChangeRequestError) {
        setCrewChangeRequestError(addCrewChangeRequestError);
        openErrorModal();
      } else {
        const requestStatus: CrewChangeRequestStatusCode = get(crewChangeRequest, 'currentStatus');
        dispatchToast(createSuccessToast(requestStatus));
        closeSideSheet(true, crewChangeRequest?.id);
      }
    },
    onError: (error) => {
      setCrewChangeRequestError(error);
      openErrorModal();
    },
    refetchQueries: ['crewChangeStats'],
  });

  const closeModal = () => {
    setCrewChangeRequestError(null);
    closeErrorModal();
  };

  const onCrewSelect = (crewMember: CrewMember, isSourceSelection: boolean) => {
    if (isSourceSelection) {
      if (selectedCrafts.length > 0) setSelectedCrafts([]);
      if (moveEntireCrew) setCrewChangeRequestMoveType(CrewChangeRequestMoveType.CRAFT);
      if (userCrewMember?.persona !== PersonaType.FOREMAN && userCrewMember?.persona !== PersonaType.GENERAL_FOREMAN) {
        if (selectedDestSuperintendent.id !== MockSuperintendentId) setSelectedDestSuperintendent(MockCrewMembers[0]);
        if (selectedDestGeneralForeman.id !== MockGeneralForemanId) setSelectedDestGeneralForeman(MockCrewMembers[1]);
        if (selectedDestForeman.id !== MockForemanId) setSelectedDestForeman(MockCrewMembers[2]);
      }
    }
    const crewMemberExt: CrewMemberExt = { ...getCrewMemberExt(crewMember) };
    switch (crewMember.persona) {
      case PersonaType.SUPERINTENDENT: {
        if (isSourceSelection) {
          setSelectedSourceSuperintendent(crewMemberExt);
          setSelectedSourceGeneralForeman(MockCrewMembers[1]);
          setSelectedSourceForeman(MockCrewMembers[2]);
        } else {
          setSelectedDestSuperintendent(crewMemberExt);
          setSelectedDestGeneralForeman(MockCrewMembers[1]);
          if (!moveEntireCrew) setSelectedDestForeman(MockCrewMembers[2]);
        }
        break;
      }
      case PersonaType.GENERAL_FOREMAN: {
        if (isSourceSelection) {
          setSelectedSourceGeneralForeman(crewMemberExt);
          setSelectedSourceForeman(MockCrewMembers[2]);
        } else {
          setSelectedDestGeneralForeman(crewMemberExt);
          if (!moveEntireCrew) setSelectedDestForeman(MockCrewMembers[2]);
        }
        break;
      }
      case PersonaType.FOREMAN: {
        if (isSourceSelection) {
          setSelectedSourceForeman(crewMemberExt);
          if (
            userCrewMember?.persona === PersonaType.SUPERINTENDENT &&
            selectedSourceSuperintendent.id !== userCrewMember?.id &&
            selectedDestSuperintendent.id !== userCrewMember?.id
          ) {
            setSelectedDestSuperintendent({ ...getCrewMemberExt(userCrewMember) });
          } else if (
            userCrewMember?.persona === PersonaType.GENERAL_FOREMAN &&
            selectedSourceGeneralForeman.id !== userCrewMember?.id &&
            selectedDestGeneralForeman.id !== userCrewMember?.id
          ) {
            setSelectedDestGeneralForeman({ ...getCrewMemberExt(userCrewMember) });
          } else if (
            userCrewMember?.persona === PersonaType.FOREMAN &&
            crewMemberExt.id !== userCrewMember?.id &&
            selectedDestForeman.id !== userCrewMember?.id
          ) {
            setSelectedDestForeman({ ...getCrewMemberExt(userCrewMember) });
          }
        } else setSelectedDestForeman(crewMemberExt);
        break;
      }
      default:
        break;
    }
  };

  const onCraftSelect = useCallback(
    (values: CrewMember[]) => {
      setSelectedCrafts(values);
    },
    [setSelectedCrafts]
  );

  const onCraftDataLoaded = (hasPendingRequestItems: boolean) => {
    if (hasPendingRequestItems) setCrewChangeRequestMoveType(CrewChangeRequestMoveType.CRAFT);
    setHasAnyPendingRequest(hasPendingRequestItems);
  };

  const onSubmit = async () => {
    if (moveEntireCrew && selectedSourceGeneralForeman.id === selectedDestGeneralForeman.id) {
      setCrewChangeRequestError(AddCrewChangeRequestCustomError.ORIGIN_GF_SAME_AS_DEST_GF);
      return;
    }
    if (!moveEntireCrew && selectedSourceForeman.id === selectedDestForeman.id) {
      setCrewChangeRequestError(AddCrewChangeRequestCustomError.ORIGIN_FM_SAME_AS_DEST_FM);
      return;
    }
    if (
      !isUserPartOfCrew(
        concat(
          selectedSourceForeman,
          selectedSourceGeneralForeman,
          selectedSourceSuperintendent,
          selectedDestForeman,
          selectedDestGeneralForeman,
          selectedDestSuperintendent
        )
      )
    ) {
      setCrewChangeRequestError(AddCrewChangeRequestCustomError.USER_NOT_IN_ORIGIN_DEST);
      return;
    }
    const addCrewChangeRequestInput: AddCrewChangeRequestInput = {
      sourceHierarchy: {
        foremanId: selectedSourceForeman.id,
        generalForemanId: selectedSourceGeneralForeman.id,
        superintendentId: selectedSourceSuperintendent.id,
        crewEmployeeIds: map(selectedCrafts, 'id'),
      },
      destinationHierarchy: {
        foremanId: selectedDestForeman.id,
        generalForemanId: selectedDestGeneralForeman.id,
        superintendentId: selectedDestSuperintendent.id,
        crewEmployeeIds: map(selectedCrafts, 'id'),
      },
      moveType: crewChangeRequestMoveType,
      comments: comments,
    };
    await addCrewChangeRequest({
      variables: {
        input: addCrewChangeRequestInput,
      },
    });
  };

  const isValid = (): boolean => {
    return (
      selectedSourceForeman.id !== MockForemanId &&
      selectedSourceGeneralForeman.id !== MockGeneralForemanId &&
      selectedSourceSuperintendent.id !== MockSuperintendentId &&
      selectedDestForeman.id !== MockForemanId &&
      selectedDestGeneralForeman.id !== MockGeneralForemanId &&
      selectedDestSuperintendent.id !== MockSuperintendentId &&
      selectedCrafts.length > 0
    );
  };

  const selectedCraftsExt: CrewMemberExt[] = map(selectedCrafts, (craftItem) => {
    return {
      ...getCrewMemberExt(craftItem),
      isAffected: false,
      showArrow: false,
      hideTreeLine: true,
      avatarSize: AvatarSize.MEDIUM,
    };
  });

  const sourceAffectedCrafts: CrewMemberExt[] = moveEntireCrew
    ? map(selectedCraftsExt, (affectedeCraft) => {
        return { ...affectedeCraft, isAffected: true, showArrow: false };
      })
    : map(selectedCraftsExt, (affectedeCraft) => {
        return { ...affectedeCraft, isAffected: true, showArrow: selectedDestForeman.id !== MockForemanId };
      });

  const sourceCrewTreeMembers: CrewMemberExt[] = [
    selectedSourceSuperintendent,
    selectedSourceGeneralForeman,
    moveEntireCrew
      ? {
          ...selectedSourceForeman,
          isAffected: selectedSourceForeman.id !== MockForemanId,
          showArrow: selectedDestForeman.id !== MockForemanId,
        }
      : {
          ...selectedSourceForeman,
          isAffected: false,
          showArrow: false,
        },
    ...(selectedSourceForeman.id !== MockForemanId ? sourceAffectedCrafts : []),
  ];

  const destCrewTreeMembers: CrewMemberExt[] = [
    selectedDestSuperintendent,
    selectedDestGeneralForeman,
    selectedDestForeman,
    ...(selectedDestForeman.id !== MockForemanId ? selectedCraftsExt : []),
  ];

  useEffect(() => {
    const selectedSourceCrewMembers: CrewMember[] = [
      selectedSourceForeman,
      selectedSourceGeneralForeman,
      selectedSourceSuperintendent,
    ];
    const selectedDestCrewMembers: CrewMember[] = [
      selectedDestForeman,
      selectedDestGeneralForeman,
      selectedDestSuperintendent,
    ];
    if (
      affectedCrewMembers.length === 0 ||
      (selectedSourceCrewMembers.length === 0 && selectedDestCrewMembers.length === 0)
    )
      return;
    forEach(affectedCrewMembers, (affectedCrewMember) => {
      const sourceCM = find(
        selectedSourceCrewMembers,
        (item: CrewMember) => item.id === affectedCrewMember.crewMemberId
      );
      const destCM = find(selectedDestCrewMembers, (item: CrewMember) => item.id === affectedCrewMember.crewMemberId);
      if (sourceCM) {
        if (sourceCM.persona === PersonaType.SUPERINTENDENT) {
          setSelectedSourceGeneralForeman(MockCrewMembers[1]);
        } else if (sourceCM.persona === PersonaType.GENERAL_FOREMAN) {
          setSelectedSourceForeman(MockCrewMembers[2]);
        }
        setSelectedDestSuperintendent(MockCrewMembers[0]);
        setSelectedDestGeneralForeman(MockCrewMembers[1]);
        setSelectedDestForeman(MockCrewMembers[2]);
      }
      if (destCM) {
        if (destCM.persona === PersonaType.SUPERINTENDENT) {
          setSelectedDestGeneralForeman(MockCrewMembers[1]);
        } else if (destCM.persona === PersonaType.GENERAL_FOREMAN) {
          setSelectedDestForeman(MockCrewMembers[2]);
        }
      }
    });
    setAffectedCrewMembers([]);
  }, [affectedCrewMembers]);

  return (
    <SideSheet size={SideSheetSize.LARGE} visible>
      <SideSheetHeader title={t`New Crew Change Request`}>
        <SideSheetClose onClick={() => closeSideSheet(false)}></SideSheetClose>
      </SideSheetHeader>
      <SideSheetBody>
        <CrewListStyled css="overflow:hidden">
          <Box flexDirection={{ sm: 'column' }} px={{ sm: '450' }} pt={{ sm: '400' }}>
            <Box mb={{ sm: '550' }}>
              <Text size="400" color="grey.1000" weight="600" family="graphik">
                <Trans>Original Crew</Trans>
              </Text>
            </Box>
            <CrewSelection
              selectedCrewMembers={concat(
                selectedSourceSuperintendent,
                selectedSourceGeneralForeman,
                selectedSourceForeman
              )}
              onCrewSelect={onCrewSelect}
              isSourceSelection={true}
              disableSuperintendent={!canSelectSuperintendent}
              disableGeneralForeman={!canSelectGeneralForeman}
            />
          </Box>
          {selectedSourceForeman && selectedSourceForeman.id !== MockForemanId && (
            <>
              <Box px={{ sm: '450' }} pt={{ sm: '500' }}>
                <Text size="300" color="grey.1000" family="graphik">
                  <Trans>Move entire crew (including Foreman?)</Trans>
                </Text>
              </Box>
              <Box pb={{ sm: '450' }} px={{ sm: '450' }} pt={{ sm: '400' }}>
                <SegmentedControlStyled
                  disabled={
                    userCrewMember?.persona === PersonaType.FOREMAN ||
                    hasPendingEntireCrewChangeRequest ||
                    hasAnyPendingRequest
                  }
                >
                  <SegmentedControlOption
                    selected={moveEntireCrew}
                    onClick={() => setCrewChangeRequestMoveType(CrewChangeRequestMoveType.CREW)}
                    style={{ height: '22px' }}
                  >
                    <Trans>Yes</Trans>
                  </SegmentedControlOption>
                  <SegmentedControlOption
                    selected={!moveEntireCrew}
                    onClick={() => setCrewChangeRequestMoveType(CrewChangeRequestMoveType.CRAFT)}
                    style={{ height: '22px' }}
                  >
                    <Trans>No</Trans>
                  </SegmentedControlOption>
                </SegmentedControlStyled>
              </Box>
              <HeaderStyled>
                <Text size="400" color="grey.1000" weight="600" family="graphik">
                  {t`Crew`} {`(${selectedSourceForeman?.crew?.crewNumber})`}
                </Text>
                {hasPendingEntireCrewChangeRequest && (
                  <Icon
                    onMouseEnter={() => setShowRequestInProgress(true)}
                    onMouseLeave={() => setShowRequestInProgress(false)}
                    ref={refElement}
                    size={IconSize.MEDIUM}
                    icon="info-circle"
                    style={{ cursor: 'pointer' }}
                    fillColor="blue.800"
                  ></Icon>
                )}
              </HeaderStyled>
            </>
          )}
          {selectedSourceForeman && selectedSourceForeman.id !== MockForemanId && (
            <CraftList
              superintendent={selectedSourceSuperintendent}
              generalForeman={selectedSourceGeneralForeman}
              foreman={selectedSourceForeman}
              onCraftSelect={onCraftSelect}
              selectedCrafts={selectedCrafts}
              crewChangeRequestMoveType={crewChangeRequestMoveType}
              onCraftDataLoaded={onCraftDataLoaded}
            />
          )}
        </CrewListStyled>
        <CrewListStyled px={{ sm: '450' }} pt={{ sm: '400' }}>
          <Box mb={{ sm: '550' }}>
            <Text size="400" color="grey.1000" weight="600" family="graphik">
              <Trans>Destination Crew</Trans>
            </Text>
          </Box>
          <CrewSelection
            selectedCrewMembers={concat(selectedDestSuperintendent, selectedDestGeneralForeman, selectedDestForeman)}
            onCrewSelect={onCrewSelect}
            isSourceSelection={false}
            moveEntireCrew={moveEntireCrew}
            disableSuperintendent={selectedSourceForeman.id === MockForemanId || !canSelectSuperintendent}
            disableGeneralForeman={selectedDestSuperintendent.id === MockSuperintendentId || !canSelectGeneralForeman}
          />
        </CrewListStyled>
        <ColStyled flexGrow={{ sm: 1 }} style={{ overflowY: 'auto' }}>
          <CrewChangeSummary sourceCrewMembers={sourceCrewTreeMembers} destinationCrewMembers={destCrewTreeMembers} />
          <HeaderStyled>
            <Text size="400" color="grey.1000" weight="600" family="graphik">
              <Trans>Comments</Trans>
            </Text>
          </HeaderStyled>
          <CommentStyled>
            <TextArea
              data-testid="comments"
              maxLength={2000}
              placeholder={t`Add a comment (optional)`}
              size={TextFieldSize.MEDIUM}
              rows="4"
              value={comments}
              css={`
                font-family: ${({ theme }) => theme.fontFamilies['graphik']};
                color: ${({ theme }) => theme.colors.grey[1000]};
              `}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setComments(event.target.value);
              }}
            ></TextArea>
          </CommentStyled>
        </ColStyled>
      </SideSheetBody>
      <SideSheetFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <FooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={() => closeSideSheet(false)}>
              <Trans>Cancel</Trans>
            </FooterButtonStyled>
          </Box>
          <Box>
            <FooterButtonStyled
              onClick={onSubmit}
              disabled={!isValid() || loading}
              appearance={ButtonAppearance.SECONDARY}
            >
              {loading && <Spinner />}
              {!loading && 'Submit'}
            </FooterButtonStyled>
          </Box>
        </Box>
      </SideSheetFooter>
      {refElement && showRequestInProgress && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <CrewChangeRequestPopover showPopper={showRequestInProgress} refElement={refElement} />
        </Suspense>
      )}
      {crewChangeRequestError && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <CrewChangeRequestError addCrewChangeError={crewChangeRequestError} close={closeModal} />
        </Suspense>
      )}
    </SideSheet>
  );
};

export default CrewChangeRequest;
